import { useCallback, useMemo } from "react";
import { z } from "zod";

import {
  ColorPickerInput,
  ContextMenu,
  Icon,
  MenuItem,
  PropertiesPanelSection,
  type TestAttribute,
} from "@skilitics/designer-ui-components";
import type { UniqueIdentifier } from "@skilitics/drag-drop";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { DesignerNode } from "../../../types";
import { useEditingCss } from "../EditingCssPropertiesContext";
import { BackgroundImages } from "./BackgroundImages";
import { BackgroundPropertiesRow } from "./BackgroundPropertiesRow";

const backgroundType = z.enum(["image", "linearGradient", "solidColor"]);

type BackgroundType = z.infer<typeof backgroundType>;

type Props = {
  onOrderChange?: (key: UniqueIdentifier, newIndex: number) => void;
  backgroundImages?: DesignerNode[];
  onAddImage?: (type: "image" | "gradient") => void;
  onRemove?: (id: number) => void;
  backgroundTypes?: BackgroundType[];
} & TestAttribute;

export const BackgroundProperties = ({
  backgroundImages = [],
  onAddImage,
  onOrderChange,
  onRemove,
  backgroundTypes = backgroundType.options,
  dataTest,
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const { cssProperties, onCssPropertiesChange } = useEditingCss();

  const hasBackgroundColor = useMemo(
    () => cssProperties.backgroundColor !== undefined, // "" is a valid value.
    [cssProperties.backgroundColor],
  );

  const updateBackgroundColor = useCallback(
    (color?: string) => {
      onCssPropertiesChange({ backgroundColor: color });
    },
    [onCssPropertiesChange],
  );

  const handleAdd = (type: BackgroundType) => {
    switch (type) {
      case "solidColor":
        updateBackgroundColor("#FFFFFF");
        break;
      case "image":
        onAddImage?.("image");
        break;
      case "linearGradient":
        onAddImage?.("gradient");
        break;
    }
  };

  const addBackgroundOptions = useMemo(() => {
    const options: BackgroundType[] = ["image", "linearGradient"];
    if (!hasBackgroundColor) {
      options.splice(1, 0, "solidColor"); // Only one background-color allowed.
    }
    return options.filter((option) => backgroundTypes.includes(option));
  }, [backgroundTypes, hasBackgroundColor]);

  const imageSupport =
    (backgroundTypes.includes("image") || backgroundTypes.includes("linearGradient")) && onRemove && onOrderChange;

  return (
    <>
      <PropertiesPanelSection
        title={formatMessage("sectionHeader")}
        controls={<BackgroundImageControls onAdd={handleAdd} menuOptions={addBackgroundOptions} />}
        empty={backgroundImages.length === 0 && !hasBackgroundColor}
        data-test={dataTest ?? "background-properties-section"}
      >
        {imageSupport && (
          <BackgroundImages images={backgroundImages} onRemove={onRemove} onOrderChange={onOrderChange} />
        )}

        {hasBackgroundColor && (
          <BackgroundPropertiesRow onRemove={() => updateBackgroundColor(undefined)}>
            <ColorPickerInput onChange={updateBackgroundColor} value={cssProperties.backgroundColor ?? ""} />
          </BackgroundPropertiesRow>
        )}
      </PropertiesPanelSection>
    </>
  );
};

const formatStrings = {
  sectionHeader: {
    en: "Background",
  },
} satisfies FormatMessages;

type BackgroundImageControlsProps = {
  onAdd: (type: BackgroundType) => void;
  menuOptions: BackgroundType[];
};

const BackgroundImageControls = ({ onAdd, menuOptions }: BackgroundImageControlsProps) => {
  const formatMessage = useFormatMessage(backgroundImageFormatStrings);

  return (
    <ContextMenu
      disabled={menuOptions.length === 0}
      IconComponent={<Icon icon="plus" />}
      data-test="background-image-controls-context-menu"
    >
      {menuOptions.map((type) => (
        <MenuItem key={type} onClick={() => onAdd(type)}>
          {formatMessage(type)}
        </MenuItem>
      ))}
    </ContextMenu>
  );
};

const backgroundImageFormatStrings = {
  image: {
    en: "Add image",
  },
  solidColor: {
    en: "Add solid color",
  },
  linearGradient: {
    en: "Add linear gradient",
  },
} satisfies FormatMessages;
